import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Rocket, Clock, RefreshCcw, LineChart, ArrowRight, CheckCircle } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import CaseStudies, { pipelineReviews } from "@/components/CaseStudies";

const PipelineAcceleration = () => {
  const benefits = [
    "Shorter sales cycles through buyer enablement",
    "Re-engage stalled deals with targeted nurture",
    "Multi-touch attribution to optimize spend",
    "Sales enablement content that advances deals",
    "Real-time intent signals for timely outreach",
    "Pipeline velocity dashboards and reporting",
  ];

  const process = [
    {
      icon: LineChart,
      title: "Pipeline Analysis",
      description: "Audit your current pipeline to identify bottlenecks and drop-off points.",
    },
    {
      icon: RefreshCcw,
      title: "Deal Re-engagement",
      description: "Launch campaigns to warm up stalled opportunities and lost deals.",
    },
    {
      icon: Clock,
      title: "Cycle Compression",
      description: "Provide buyers the content they need to move faster through their journey.",
    },
    {
      icon: Rocket,
      title: "Velocity Optimization",
      description: "Continuously improve conversion rates at every stage of your funnel.",
    },
  ];

  return (
    <>
      <EnhancedSEO
        type="service"
        title="Pipeline Acceleration | VGP - Speed Up Your B2B Sales Cycle"
        description="Accelerate pipeline velocity with buyer enablement, deal re-engagement, and conversion optimization. Close more deals faster."
        keywords="pipeline acceleration, sales cycle, B2B sales, buyer enablement, deal velocity"
        url="/pipeline-acceleration"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-6">
                Pipeline Acceleration
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Close Deals <span className="text-primary">Faster</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                You have pipeline. Now let's move it. Accelerate deal velocity, re-engage stalled opportunities, and compress your sales cycle.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="premium" asChild>
                  <Link to="/book-call">
                    Accelerate Your Pipeline
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">See Pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                What You Get
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Our Acceleration Process
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                A data-driven approach to moving deals through your funnel faster.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Typical Results
              </h2>
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">30%</div>
                  <p className="text-muted-foreground">Reduction in average sales cycle</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">25%</div>
                  <p className="text-muted-foreground">Of stalled deals re-engaged</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">2x</div>
                  <p className="text-muted-foreground">Improvement in stage conversion</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <CaseStudies 
          title="Pipeline Acceleration Results"
          subtitle="See how we've helped B2B companies close deals faster."
          reviews={pipelineReviews}
        />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Move Deals Faster?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Let's analyze your pipeline and identify the biggest opportunities for acceleration.
              </p>
              <Button size="lg" variant="premium" asChild>
                <Link to="/book-call">
                  Get Your Pipeline Analysis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PipelineAcceleration;